import { Button, Slider } from '@mui/material'
import { findApriorMapVersion } from 'api/ApriorMapApi'
import useAllApriorMaps from 'hooks/useAllApriorMaps'
import { Extent } from 'ol/extent'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover } from 'react-tiny-popover'
import useMapStore from 'store/map'
import useMissionStore from 'store/mission'
import { IMissionInfo } from 'utils/interfaces'

import ApriorMapLayer from './ApriorMapLayer'
import styles from './ApriorMapToggle.module.css'

interface Props {
  hideButton?: boolean
  onExtentChange?: (extent: Extent | null, centerOnLoad: boolean) => void
  missionInfo?: IMissionInfo | null
}

const ApriorMapToggle = ({ hideButton = false, onExtentChange, missionInfo }: Props) => {
  const { t } = useTranslation()
  const apriorMapVisible = useMapStore(s => s.apriorMapVisible)
  const apriorMapOpacity = useMapStore(s => s.apriorMapOpacity)
  const editorTempApriorMapVersionId = useMapStore(s => s.tempApriorMapVersionId)
  const toggleApriorMap = useMapStore(s => s.toggleApriorMap)
  const setApriorMapOpacity = useMapStore(s => s.setApriorMapOpacity)
  const resetApriorMapDisplay = useMapStore(s => s.resetApriorMapDisplay)
  const editorMissionId = useMissionStore(s => s.missionInfo?.id)
  const editorSavedVersionId = useMissionStore(s => s.missionInfo?.apriorMapVersionId)
  const missionId = missionInfo === undefined ? editorMissionId : missionInfo?.id
  const savedVersionId = missionInfo === undefined ? editorSavedVersionId : missionInfo?.apriorMapVersionId
  const tempApriorMapVersionId = missionInfo === undefined ? editorTempApriorMapVersionId : undefined
  const displayedVersionId = tempApriorMapVersionId === undefined ? savedVersionId : tempApriorMapVersionId
  const previousDisplayedMapRef = useRef({ missionId, displayedVersionId })
  const centerOnLoad =
    previousDisplayedMapRef.current.missionId === missionId &&
    previousDisplayedMapRef.current.displayedVersionId !== displayedVersionId
  const { data: maps = [], isPending } = useAllApriorMaps()
  const apriorMapVersion = findApriorMapVersion(maps, displayedVersionId)
  const showControl = !!apriorMapVersion?.tifUrl && !hideButton
  const unavailable = !!displayedVersionId && !isPending && !apriorMapVersion?.tifUrl
  const sourceKey = apriorMapVersion?.tifUrl
    ? `${apriorMapVersion.tifUrl}:${apriorMapVersion.ovrUrl ?? ''}`
    : null
  const [isOpen, setIsOpen] = useState(false)
  const [failedSourceKey, setFailedSourceKey] = useState<string | null>(null)
  const hasLoadError = unavailable || (sourceKey !== null && failedSourceKey === sourceKey)

  useEffect(() => {
    previousDisplayedMapRef.current = { missionId, displayedVersionId }
  }, [missionId, displayedVersionId])

  useEffect(() => {
    setIsOpen(false)
    resetApriorMapDisplay()
  }, [missionId, savedVersionId, resetApriorMapDisplay])

  useEffect(() => {
    if (!showControl) setIsOpen(false)
  }, [showControl])

  return (
    <>
      {displayedVersionId && (
        <ApriorMapLayer
          key={`${missionId ?? 'new'}:${displayedVersionId}`}
          url={apriorMapVersion?.tifUrl}
          ovrUrl={apriorMapVersion?.ovrUrl}
          visible={apriorMapVisible}
          opacity={apriorMapOpacity}
          onExtentChange={onExtentChange}
          onError={() => setFailedSourceKey(sourceKey)}
          centerOnLoad={centerOnLoad}
        />
      )}
      {!hideButton && hasLoadError && (
        <div role="alert" className={styles.error}>
          {t('missionView.apriorMapLoadError')}
        </div>
      )}
      {showControl && (
        <div className="ol-unselectable ol-control-custom">
          <Popover
            isOpen={isOpen}
            onClickOutside={() => setIsOpen(false)}
            positions={['left']}
            containerStyle={{ zIndex: '999' }}
            content={
              <div className={styles.panel}>
                <div className={styles.title}>{t('missionView.apriorMap')}</div>
                <Button variant="contained" fullWidth onClick={toggleApriorMap} aria-pressed={apriorMapVisible}>
                  {apriorMapVisible ? t('missionView.apriorMapHide') : t('missionView.apriorMapShow')}
                </Button>
                <div className={styles.opacityLabel}>
                  <span>{t('missionView.opacity')}</span>
                  <span>{Math.round(apriorMapOpacity * 100)}%</span>
                </div>
                <Slider
                  aria-label={t('missionView.opacity')}
                  value={apriorMapOpacity}
                  onChange={(_, value) => setApriorMapOpacity(value as number)}
                  min={0}
                  max={1}
                  step={0.05}
                  disabled={!apriorMapVisible}
                />
              </div>
            }
          >
            <button
              type="button"
              className={styles.icon}
              aria-label={t('missionView.apriorMap')}
              aria-expanded={isOpen}
              aria-pressed={apriorMapVisible}
              onClick={() => setIsOpen(open => !open)}
            />
          </Popover>
        </div>
      )}
    </>
  )
}

export default ApriorMapToggle
